import { useEffect } from 'react';

const SITE_NAME = 'MyBrickLog';
const DEFAULT_DESCRIPTION = 'Track, organize, and value your entire LEGO® collection for free. Browse 25,000+ sets across every theme, check retail and aftermarket prices for retired sets, manage your wishlist, and track minifigures.';

const upsertMeta = (selector, attributes) => {
    let tag = document.head.querySelector(selector);
    if (!tag) {
        tag = document.createElement('meta');
        document.head.appendChild(tag);
    }
    Object.entries(attributes).forEach(([key, value]) => {
        tag.setAttribute(key, value);
    });
    return tag;
};

const upsertLink = (selector, attributes) => {
    let tag = document.head.querySelector(selector);
    if (!tag) {
        tag = document.createElement('link');
        document.head.appendChild(tag);
    }
    Object.entries(attributes).forEach(([key, value]) => {
        tag.setAttribute(key, value);
    });
    return tag;
};

/**
 * Sets document.title to "Title | MyBrickLog". Pass null/undefined/empty to use site name only.
 * Optionally pass a description string to update the meta description tag for that page.
 * Optional third argument supports richer SEO metadata:
 * {
 *   canonicalUrl?: string,
 *   ogType?: string,
 *   ogImage?: string,
 *   noindex?: boolean
 * }
 */
export function useDocumentTitle(title, description, options = {}) {
    const {
        canonicalUrl,
        ogType = 'website',
        ogImage,
        noindex = false
    } = options;

    const resolvedTitle = title == null || title === '' ? SITE_NAME : `${title} | ${SITE_NAME}`;
    const resolvedDescription = description || DEFAULT_DESCRIPTION;
    const resolvedCanonical = canonicalUrl || (typeof window !== 'undefined' ? window.location.href : null);
    const resolvedImage = ogImage || '/thumbnail.png';

    useEffect(() => {
        document.title = resolvedTitle;
    }, [resolvedTitle]);

    useEffect(() => {
        upsertMeta('meta[name="description"]', { name: 'description', content: resolvedDescription });
        upsertMeta('meta[property="og:title"]', { property: 'og:title', content: resolvedTitle });
        upsertMeta('meta[property="og:description"]', { property: 'og:description', content: resolvedDescription });
        upsertMeta('meta[property="og:type"]', { property: 'og:type', content: ogType });
        upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: resolvedTitle });
        upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: resolvedDescription });
        upsertMeta('meta[name="robots"]', { name: 'robots', content: noindex ? 'noindex, nofollow' : 'index, follow' });

        if (resolvedCanonical) {
            upsertLink('link[rel="canonical"]', { rel: 'canonical', href: resolvedCanonical });
            upsertMeta('meta[property="og:url"]', { property: 'og:url', content: resolvedCanonical });
        }

        upsertMeta('meta[property="og:image"]', { property: 'og:image', content: resolvedImage });
        upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: resolvedImage });

        return () => {
            document.title = SITE_NAME;
            upsertMeta('meta[name="description"]', { name: 'description', content: DEFAULT_DESCRIPTION });
            upsertMeta('meta[property="og:title"]', { property: 'og:title', content: 'MyBrickLog – Free LEGO® Collection Tracker & Price Guide' });
            upsertMeta('meta[property="og:description"]', { property: 'og:description', content: 'Track, organize, and value your entire LEGO® collection for free. Browse 25,000+ sets, check prices for retired sets, manage wishlists, and track minifigures.' });
            upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
            upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: 'MyBrickLog – Free LEGO® Collection Tracker & Price Guide' });
            upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: 'Track your entire LEGO® collection for free. Browse 25,000+ sets, check prices for retired sets, and manage your wishlist.' });
            upsertMeta('meta[name="robots"]', { name: 'robots', content: 'index, follow' });
            upsertLink('link[rel="canonical"]', { rel: 'canonical', href: 'https://www.mybricklog.com/' });
            upsertMeta('meta[property="og:url"]', { property: 'og:url', content: 'https://www.mybricklog.com/' });
            upsertMeta('meta[property="og:image"]', { property: 'og:image', content: '/thumbnail.png' });
            upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: '/thumbnail.png' });
        };
    }, [resolvedTitle, resolvedDescription, resolvedCanonical, ogType, resolvedImage, noindex]);
}

export { SITE_NAME };
